(function () {
  'use strict';

  /* ============================================================
     COREVIAN OPERATING SYSTEM
     Executive Governance & Institutional Knowledge

     Controlled source:
     The Corevian Bible™
     CVN-BIBLE-001 · Version 3.0

     ACCESS:
     Raquia Compass — Founder & Chief Executive Officer ONLY

     ────────────────────────────────────────────────────────────
     SECURITY/DATA-ACCESS MIGRATION (this revision):
     loadControlledBibleRecord() and loadBibleSections() — the two
     direct browser Supabase queries against ceo_bible_documents and
     ceo_bible_sections — have been removed. This was the confirmed
     production bypass: the full Bible rendered with no Bible session
     present at all, since RLS on those tables currently still permits
     direct client reads (that RLS tightening is a separate, later
     step — not yet done — this migration is what makes it safe to do).

     Content is now fetched through get-bible-content, which requires
     a valid Bible session (not merely CEO auth) before returning
     anything. Since no such session previously existed anywhere in
     this file's flow, opening the Operating System now requires one
     minimal new step — a TOTP code prompt — chaining the existing,
     already-deployed request-bible-challenge and verify-bible-challenge
     functions. Everything else in this file — the viewer, navigation,
     section rendering, styling, workspace lifecycle — is unchanged.
     ============================================================ */

  const COS = {
    authorizedUserId: 'e61d0c96-b6d1-4c09-bead-51c0103044a2',
    documentCode: 'CVN-BIBLE-001',
    version: '3.0',
    supabaseUrl: 'https://xoolcbbpnvdylcoorlqg.supabase.co',
    publishableKey: 'sb_publishable_bjOrV5qp2n0PtuVFOSA_6g_3Micws2T'
  };

  let operatingSystemInstalled = false;
  let currentSections = [];

  // Held only in memory for the life of this page — never written to
  // localStorage/sessionStorage. The server's own absolute cap and
  // idle timeout remain the real authority; if a held token is
  // rejected, the code below clears it and prompts for a fresh one.
  let heldBibleSessionToken = null;


  /* ============================================================
     DEMO MODE GUARD — unchanged from the existing file.
     ============================================================ */

  function isDemoMode() {
    if (window.demoMode === true) return true;

    const banner = document.getElementById('demoBanner');
    if (!banner) return false;

    return window.getComputedStyle(banner).display !== 'none';
  }


  /* ============================================================
     AUTHENTICATION — unchanged from the existing file.
     ============================================================ */

  function getUniversityClient() {
    try {
      if (typeof window.getCorevianSupabaseClient !== 'function') {
        console.error(
          'Corevian Operating System: University Supabase client unavailable.'
        );
        return null;
      }

      return window.getCorevianSupabaseClient();

    } catch (error) {
      console.error(
        'Corevian Operating System client error:',
        error
      );
      return null;
    }
  }


  async function getAuthenticatedSession() {
    const client = getUniversityClient();

    if (!client) return null;

    const { data, error } = await client.auth.getSession();

    if (error) throw error;

    return data?.session || null;
  }


  function isAuthorizedCEO(session) {
    return session?.user?.id === COS.authorizedUserId;
  }


  /* ============================================================
     PROTECTED CONTENT / CHALLENGE CALLS — new. Raw fetch, matching
     the established pattern used in enroll-bible-totp and the
     enrollment UI, rather than supabase-js's functions.invoke().
     ============================================================ */

  async function callFunction(name, payload) {
    const session = await getAuthenticatedSession();
    if (!session || !session.access_token) {
      throw new Error('Authentication session not available.');
    }

    const response = await fetch(`${COS.supabaseUrl}/functions/v1/${name}`, {
      method: 'POST',
      headers: {
        apikey: COS.publishableKey,
        Authorization: `Bearer ${session.access_token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload || {})
    });

    const text = await response.text();
    let body;
    try { body = text ? JSON.parse(text) : null; } catch { body = null; }

    return { ok: response.ok, status: response.status, body };
  }

  const callGetBibleContent = (bibleSessionToken) =>
    callFunction('get-bible-content', { bibleSessionToken });

  const callRequestChallenge = () => callFunction('request-bible-challenge', {});

  const callVerifyChallenge = (attemptId, code) =>
    callFunction('verify-bible-challenge', { attemptId, code });


  /* ============================================================
     SAFE HTML SANITIZER — unchanged from the existing file.
     ============================================================ */

  function sanitizeControlledHTML(html) {
    const template = document.createElement('template');

    template.innerHTML = String(html || '');

    const allowedTags = new Set([
      'SECTION',
      'DIV',
      'P',
      'H2',
      'H3',
      'H4',
      'UL',
      'OL',
      'LI',
      'TABLE',
      'THEAD',
      'TBODY',
      'TR',
      'TH',
      'TD',
      'STRONG',
      'EM',
      'BLOCKQUOTE',
      'HR',
      'BR'
    ]);

    const allowedAttributes = new Set([
      'class'
    ]);

    const nodes = Array.from(
      template.content.querySelectorAll('*')
    );

    nodes.forEach((node) => {
      if (!allowedTags.has(node.tagName)) {
        node.replaceWith(
          document.createTextNode(node.textContent || '')
        );
        return;
      }

      Array.from(node.attributes).forEach((attribute) => {
        const attributeName = attribute.name.toLowerCase();

        if (
          !allowedAttributes.has(attributeName) ||
          attributeName.startsWith('on')
        ) {
          node.removeAttribute(attribute.name);
        }
      });
    });

    return template.innerHTML;
  }


  /* ============================================================
     DISPLAY HELPERS — unchanged from the existing file.
     ============================================================ */

  function escapeHTML(value) {
    return String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }


  function romanPart(number) {
    const map = {
      1: 'ONE',
      2: 'TWO',
      3: 'THREE',
      4: 'FOUR',
      5: 'FIVE',
      6: 'SIX',
      7: 'SEVEN'
    };

    return map[number] || String(number || '');
  }


  function getPartTitle(partNumber, sections) {
    const match = sections.find(
      (section) =>
        Number(section.part_number) === Number(partNumber) &&
        section.part_title
    );

    return match?.part_title || `Part ${partNumber}`;
  }


  function getSectionByKey(sectionKey) {
    return currentSections.find(
      (section) => section.section_key === sectionKey
    ) || null;
  }


  function getFirstSectionForPart(partNumber) {
    return currentSections.find(
      (section) =>
        Number(section.part_number) === partNumber
    ) || null;
  }


  /* ============================================================
     PHASE 3 — EXECUTIVE KNOWLEDGE CENTER STATE
     Layer A (auth, session, challenge flow, get-bible-content calls)
     above this point is completely unchanged. Everything below is
     new Layer B: presentation only, built against the approved
     architectural clarification —
       Primary states: landing | reading
       Overlays: search, contents (drawer)
       No permanent chapter/Contents column. No new left rail.
       No theme toggle. No new editorial content. Real Bible data only.
     ============================================================ */

  let currentRecord = null;
  let currentView = 'landing';
  let currentSectionKey = null;
  let expandedPartNumber = null;
  let searchIndex = [];
  let activeOverlay = null;
  let lastFocusedElement = null;
  let globalKeyHandlerInstalled = false;


  function stripHtmlToText(html) {
    const template = document.createElement('template');
    template.innerHTML = String(html || '');
    return (template.content.textContent || '').replace(/\s+/g, ' ').trim();
  }

  function buildSearchIndex(sections) {
    searchIndex = sections.map((section) => ({
      section,
      haystack: [
        section.display_title,
        section.part_title,
        section.chapter_title,
        stripHtmlToText(section.content_html)
      ].filter(Boolean).join(' ').toLowerCase()
    }));
  }

  function groupSectionsByPart(sections) {
    const parts = [];
    for (let partNumber = 1; partNumber <= 7; partNumber += 1) {
      const partSections = sections.filter((s) => Number(s.part_number) === partNumber);
      if (partSections.length) {
        parts.push({ partNumber, title: getPartTitle(partNumber, partSections), sections: partSections });
      }
    }
    const appendix = sections.find((s) => s.section_type === 'appendix');
    return { parts, appendix };
  }

  function currentPartNumberOf(sectionKey) {
    const section = getSectionByKey(sectionKey);
    return section ? (Number(section.part_number) || null) : null;
  }

  // ── Presentation-only helpers. None of these alter section data; they
  // only decide how already-fetched titles are displayed. ──
  function romanNumeralFor(n) {
    const map = { 1: 'I', 2: 'II', 3: 'III', 4: 'IV', 5: 'V', 6: 'VI', 7: 'VII' };
    return map[Number(n)] || String(n || '');
  }

  // Part titles are stored as e.g. "PART II — The Corevian Story &
  // Identity". The Roman numeral is shown separately, so the repeated
  // prefix is dropped for display only.
  function stripPartPrefix(title) {
    return String(title || '').replace(/^\s*PART\s+[IVXLC\d]+\s*[\u2014\u2013:\-]\s*/i, '').trim();
  }

  function isPartOverviewTitle(displayTitle) {
    return /^\s*PART\s+[IVXLC\d]+\s*[\u2014\u2013:\-]/i.test(String(displayTitle || ''));
  }

  function parseChapterLabel(displayTitle) {
    const m = /^\s*(\d+)\.\s*(.+)$/.exec(String(displayTitle || ''));
    return m ? { num: m[1], text: m[2] } : { num: '', text: String(displayTitle || '') };
  }

  function partRomanLabel(section) {
    if (Number(section.part_number)) return `Part ${romanNumeralFor(section.part_number)}`;
    if (section.section_type === 'appendix') return 'Appendix';
    return 'Overview';
  }

  function sectionShortTitle(section) {
    if (isPartOverviewTitle(section.display_title) && Number(section.part_number)) {
      return `Part ${romanNumeralFor(section.part_number)} overview`;
    }
    return section.display_title;
  }

  function contextLabelFor(section) {
    if (Number(section.part_number)) {
      const title = stripPartPrefix(section.part_title);
      return title
        ? `Part ${romanNumeralFor(section.part_number)} \u00b7 ${title}`
        : `Part ${romanNumeralFor(section.part_number)}`;
    }
    if (section.section_type === 'appendix') return 'Appendix';
    return 'Overview';
  }


  /* ============================================================
     STYLES — Phase 3. cvn-os-shell / cvn-os-status / the challenge
     prompt classes are kept, unchanged, since renderChallengePrompt()
     and renderViewerError() (both untouched, above this point in the
     file) still depend on them. Everything else is new.
     ============================================================ */

  function installStyles() {
    if (document.getElementById('cvnOperatingSystemStyles')) return;

    const style = document.createElement('style');

    style.id = 'cvnOperatingSystemStyles';

    style.textContent = `
      .cvn-os-shell { width: 100%; max-width: 1500px; margin: 0 auto; padding: 4px 0 60px; }
      .cvn-os-status { padding: 36px; border: 1px dashed rgba(99,208,203,.28); border-radius: 14px; background: rgba(99,208,203,.035); color: #9fb2c8; line-height: 1.7; }
      .cvn-os-status strong { display: block; margin-bottom: 7px; color: #edf4ff; }
      .cvn-os-challenge-field { max-width: 320px; margin: 22px auto 0; }
      .cvn-os-challenge-error { max-width: 320px; margin: 16px auto 0; color: #fca5a5; font-size: 13px; }
      .cvn-os-challenge-input { width: 100%; box-sizing: border-box; padding: 12px 14px; border-radius: 8px; border: 1px solid rgba(99,208,203,.30); background: rgba(255,255,255,.04); color: #fff; font-size: 16px; letter-spacing: 2px; text-align: center; font-family: monospace; }
      .cvn-os-challenge-btn { display: inline-flex; margin-top: 14px; padding: 11px 22px; border-radius: 8px; font-weight: 800; font-size: 13px; cursor: pointer; border: none; background: #19B5B0; color: #071526; }

      /* ─────────────────────────────────────────────────────────────
         LANDING + READING LAYOUT
         Every rule below is scoped under #contentInner and forces its
         spacing with !important. Production screenshots showed margin
         and padding declared as plain class rules being ignored on the
         live page (canvas padding, paragraph margins, row padding and
         auto-centring all had no effect while colours and font sizes
         did), which is consistent with a host-page rule that wins on
         specificity. Scoping under an ID and forcing spacing makes this
         layout independent of that. The cause itself has not been
         confirmed in DevTools; this is built to work either way.
         ───────────────────────────────────────────────────────────── */
      /* The workspace toolbar's own rules live in the lifecycle styles and
         carry no spacing protection. Only its padding/margin are restated
         here (same values as the original); its sticky behaviour and
         click handling are not touched. */
      #contentInner .cvn-os-shell .cvn-os-toolbar { margin: 0 0 12px !important; }
      #contentInner .cvn-os-shell .cvn-os-toolbar-btn { padding: 9px 14px !important; }
      #contentInner .cvn-os-shell .cvn-ekc-landing { display: block !important; box-sizing: border-box !important; width: 100% !important; max-width: 880px !important; margin: 0 auto !important; padding: 12px 0 56px !important; }
      #contentInner .cvn-os-shell .cvn-ekc-eyebrow { display: block; margin: 0 0 16px !important; color: #63d0cb; font-size: 11px; font-weight: 600; letter-spacing: .12em; text-transform: uppercase; }
      #contentInner .cvn-os-shell .cvn-ekc-heading { margin: 0 0 8px !important; color: #fff; font-family: Georgia, Cambria, 'Times New Roman', serif; font-size: clamp(32px, 4vw, 44px); line-height: 1.1; letter-spacing: -.01em; }
      #contentInner .cvn-os-shell .cvn-ekc-heading-mark { color: #63d0cb; }
      #contentInner .cvn-os-shell .cvn-ekc-subheading { margin: 0 0 30px !important; color: #8698ae; font-size: 15px; }
      #contentInner .cvn-os-shell .cvn-ekc-search-wrap { margin: 0 0 16px !important; }
      #contentInner .cvn-os-shell .cvn-ekc-search-input { display: flex; justify-content: space-between; align-items: center; box-sizing: border-box; width: 100%; height: 54px; padding: 0 18px !important; border-radius: 12px; border: 1px solid rgba(135,159,188,.28); background: rgba(255,255,255,.04); color: #9db0c7; font-size: 15px; cursor: pointer; text-align: left; font-family: inherit; }
      #contentInner .cvn-os-shell .cvn-ekc-search-input:hover, #contentInner .cvn-os-shell .cvn-ekc-search-input:focus-visible { border-color: rgba(99,208,203,.5); color: #cdd8e6; }
      #contentInner .cvn-os-shell .cvn-ekc-search-hint { padding: 2px 8px !important; border: 1px solid rgba(135,159,188,.3); border-radius: 5px; color: #8698ae; font-size: 11px; }
      #contentInner .cvn-os-shell .cvn-ekc-continue-btn { display: flex; justify-content: space-between; align-items: center; box-sizing: border-box; width: 100%; margin: 0 0 34px !important; padding: 18px 22px !important; border: 1px solid rgba(99,208,203,.3); border-left: 3px solid #63d0cb; border-radius: 12px; background: rgba(99,208,203,.06); cursor: pointer; text-align: left; font-family: inherit; }
      #contentInner .cvn-os-shell .cvn-ekc-continue-btn:hover, #contentInner .cvn-os-shell .cvn-ekc-continue-btn:focus-visible { background: rgba(99,208,203,.12); }
      #contentInner .cvn-os-shell .cvn-ekc-continue-text { display: block; min-width: 0; }
      #contentInner .cvn-os-shell .cvn-ekc-continue-label { display: block; margin: 0 0 5px !important; color: #63d0cb; font-size: 10.5px; font-weight: 600; letter-spacing: .1em; text-transform: uppercase; }
      #contentInner .cvn-os-shell .cvn-ekc-continue-title { display: block; color: #edf4ff; font-size: 15px; font-weight: 600; line-height: 1.4; }
      #contentInner .cvn-os-shell .cvn-ekc-continue-context { display: block; margin: 3px 0 0 !important; color: #8698ae; font-size: 12.5px; }
      #contentInner .cvn-os-shell .cvn-ekc-continue-arrow { flex: 0 0 auto; margin-left: 16px !important; color: #63d0cb; font-size: 20px; }

      #contentInner .cvn-os-shell .cvn-ekc-index { border-top: 1px solid rgba(135,159,188,.18); }
      #contentInner .cvn-os-shell .cvn-ekc-index-row { border-bottom: 1px solid rgba(135,159,188,.18); }
      #contentInner .cvn-os-shell .cvn-ekc-index-row-main { display: grid; grid-template-columns: 52px 1fr auto 24px; align-items: center; gap: 14px; box-sizing: border-box; width: 100%; padding: 20px 6px !important; border: none; background: none; cursor: pointer; text-align: left; font-family: inherit; }
      #contentInner .cvn-os-shell .cvn-ekc-index-row-main:hover, #contentInner .cvn-os-shell .cvn-ekc-index-row-main:focus-visible { background: rgba(99,208,203,.05); }
      #contentInner .cvn-os-shell .cvn-ekc-index-numeral { color: #63d0cb; font-family: Georgia, Cambria, 'Times New Roman', serif; font-size: 20px; }
      #contentInner .cvn-os-shell .cvn-ekc-index-numeral-small { font-family: inherit; font-size: 12px; font-weight: 600; letter-spacing: .04em; }
      #contentInner .cvn-os-shell .cvn-ekc-index-title { color: #edf4ff; font-size: 16px; font-weight: 600; line-height: 1.35; }
      #contentInner .cvn-os-shell .cvn-ekc-index-meta { color: #7a8ea6; font-size: 12.5px; white-space: nowrap; }
      #contentInner .cvn-os-shell .cvn-ekc-index-chevron { color: #63d0cb; font-size: 18px; text-align: center; }
      #contentInner .cvn-os-shell .cvn-ekc-index-chapters { display: flex; flex-direction: column; gap: 2px; margin: 0 0 4px 58px !important; padding: 0 6px 18px 14px !important; border-left: 1px solid rgba(99,208,203,.18); }
      #contentInner .cvn-os-shell .cvn-ekc-index-chapter-btn { display: grid; grid-template-columns: 34px 1fr; gap: 8px; box-sizing: border-box; width: 100%; padding: 10px 12px !important; border: 1px solid transparent; border-radius: 8px; background: none; color: #a9b9cc; font-size: 14px; line-height: 1.4; cursor: pointer; text-align: left; font-family: inherit; }
      #contentInner .cvn-os-shell .cvn-ekc-index-chapter-btn:hover, #contentInner .cvn-os-shell .cvn-ekc-index-chapter-btn:focus-visible { border-color: rgba(99,208,203,.3); background: rgba(99,208,203,.05); color: #71d5d0; }
      #contentInner .cvn-os-shell .cvn-ekc-index-chapter-btn.is-current { background: rgba(99,208,203,.1); color: #fff; }
      #contentInner .cvn-os-shell .cvn-ekc-chapter-num { padding-top: 1px !important; color: #63d0cb; font-size: 12.5px; }
      #contentInner .cvn-os-shell .cvn-ekc-chapter-overview { color: #7a8ea6; }

      #contentInner .cvn-os-shell .cvn-ekc-reading-shell { display: block !important; box-sizing: border-box !important; width: 100% !important; max-width: 1000px !important; margin: 0 auto !important; padding: 0 0 64px !important; overflow-x: clip; }
      #contentInner .cvn-os-shell .cvn-ekc-context-bar { position: sticky; top: 42px; z-index: 10; display: flex; align-items: center; gap: 12px; box-sizing: border-box; margin: 0 0 26px !important; padding: 12px 6px !important; background: rgba(11,20,38,.96); backdrop-filter: blur(6px); min-width: 0; }
      #contentInner .cvn-os-shell .cvn-ekc-back-btn { flex: 0 0 auto; padding: 9px 15px !important; border: 1px solid rgba(99,208,203,.3); border-radius: 8px; background: rgba(99,208,203,.08); color: #71d5d0; font-size: 13px; font-weight: 600; white-space: nowrap; cursor: pointer; font-family: inherit; }
      #contentInner .cvn-os-shell .cvn-ekc-back-btn:hover, #contentInner .cvn-os-shell .cvn-ekc-back-btn:focus-visible { background: rgba(99,208,203,.16); border-color: rgba(99,208,203,.45); }
      #contentInner .cvn-os-shell .cvn-ekc-context-label { flex: 1 1 auto; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; color: #8698ae; font-size: 12.5px; }
      #contentInner .cvn-os-shell .cvn-ekc-context-actions { flex: 0 0 auto; display: flex; gap: 8px; }
      #contentInner .cvn-os-shell .cvn-ekc-icon-btn { padding: 8px 13px !important; border: 1px solid rgba(135,159,188,.25); border-radius: 8px; background: rgba(255,255,255,.04); color: #a9b9cc; font-size: 12.5px; white-space: nowrap; cursor: pointer; font-family: inherit; }
      #contentInner .cvn-os-shell .cvn-ekc-icon-btn:hover, #contentInner .cvn-os-shell .cvn-ekc-icon-btn:focus-visible { border-color: rgba(99,208,203,.4); color: #71d5d0; }

      #contentInner .cvn-os-shell .cvn-ekc-canvas { display: block !important; box-sizing: border-box !important; width: 100% !important; max-width: 1000px !important; margin: 0 auto !important; padding: 46px 60px 54px !important; border-radius: 16px; background: #F6F8FA; box-shadow: 0 18px 42px rgba(0,0,0,.35); }
      #contentInner .cvn-os-shell .cvn-ekc-canvas-badge { display: inline-block; margin: 0 0 20px !important; padding: 5px 12px !important; border-radius: 999px; background: #E4ECF2; color: #35506a; font-size: 10.5px; font-weight: 700; letter-spacing: .08em; text-transform: uppercase; }

      #contentInner .cvn-os-shell .cvn-os-document { color: #1c2733 !important; font-size: 17px; line-height: 1.8; overflow-wrap: anywhere; }
      #contentInner .cvn-os-shell .cvn-os-document p, #contentInner .cvn-os-shell .cvn-os-document li { color: #1c2733 !important; font-size: 17px !important; line-height: 1.8 !important; }
      #contentInner .cvn-os-shell .cvn-os-document p { max-width: 720px !important; margin: 0 0 20px !important; }
      #contentInner .cvn-os-shell .cvn-os-document ul, #contentInner .cvn-os-shell .cvn-os-document ol { max-width: 720px !important; margin: 0 0 24px !important; padding-left: 26px !important; }
      #contentInner .cvn-os-shell .cvn-os-document li { margin: 0 0 9px !important; }
      #contentInner .cvn-os-shell .cvn-os-document h2, #contentInner .cvn-os-shell .cvn-os-document h3, #contentInner .cvn-os-shell .cvn-os-document h4 { max-width: 720px; font-family: Georgia, Cambria, 'Times New Roman', serif; }
      #contentInner .cvn-os-shell .cvn-os-document h2 { margin: 0 0 22px !important; color: #0d151f !important; font-size: 30px; line-height: 1.2; }
      #contentInner .cvn-os-shell .cvn-os-document h3 { margin: 40px 0 14px !important; color: #14202e !important; font-size: 22px; line-height: 1.3; }
      #contentInner .cvn-os-shell .cvn-os-document h4 { margin: 28px 0 10px !important; color: #1c2c3c !important; font-size: 18px; }
      #contentInner .cvn-os-shell .cvn-os-document strong { color: #0d151f !important; }
      #contentInner .cvn-os-shell .cvn-os-document blockquote { max-width: 720px; margin: 26px 0 !important; padding: 16px 22px !important; border-left: 3px solid #0F6E56; background: #EAF3F0; color: #1c2c3c !important; }
      #contentInner .cvn-os-shell .cvn-os-document hr { margin: 32px 0 !important; border: 0; height: 1px; background: #DCE4EA; }
      #contentInner .cvn-os-shell .cvn-os-document .cvn-bible-part-label { display: block; margin: 0 0 10px !important; color: #0F6E56 !important; font-size: 11.5px !important; font-weight: 700; letter-spacing: .1em; text-transform: uppercase; }
      #contentInner .cvn-os-shell .cvn-os-document .cvn-bible-part-intro { max-width: 720px !important; margin: 0 0 28px !important; color: #445465 !important; font-size: 18px !important; line-height: 1.6 !important; }

      /* Tables. On desktop the table is not inside any scroll container:
         table-layout: fixed plus word-breaking means it cannot exceed
         its container, and having no scroll container is what lets the
         sticky header stick to the page. An earlier version of this
         file claimed overflow-y: clip avoided that; that was wrong —
         per the CSS overflow spec, clip computes to hidden when the
         other axis is auto, which made the wrapper a scroll container
         and broke the sticky header. Tables only scroll sideways on
         small screens (see the 720px query), where a fixed layout would
         squeeze columns too far. */
      #contentInner .cvn-os-shell .cvn-ekc-table-scroll { margin: 28px 0 34px !important; border: 1px solid #D3DBE3; border-radius: 10px; overflow: clip; }
      #contentInner .cvn-os-shell .cvn-os-document table { width: 100% !important; max-width: 100% !important; margin: 0 !important; border-collapse: separate; border-spacing: 0; table-layout: fixed; }
      #contentInner .cvn-os-shell .cvn-os-document th, #contentInner .cvn-os-shell .cvn-os-document td { padding: 14px 18px !important; border-bottom: 1px solid #E1E7EC; border-right: 1px solid #E1E7EC; color: #1c2733 !important; font-size: 14.5px !important; line-height: 1.55 !important; text-align: left !important; vertical-align: top; overflow-wrap: break-word; word-break: break-word; }
      #contentInner .cvn-os-shell .cvn-os-document th { position: sticky; top: 100px; z-index: 2; background: #E1E7EC; border-bottom: 3px solid #0F6E56; color: #0d151f !important; font-size: 11.5px !important; font-weight: 700; letter-spacing: .05em; text-transform: uppercase; }
      #contentInner .cvn-os-shell .cvn-os-document td:first-child { color: #0d151f !important; font-weight: 600; }
      #contentInner .cvn-os-shell .cvn-os-document tbody tr:nth-child(even) td { background: #E7ECF1; }
      #contentInner .cvn-os-shell .cvn-os-document th:last-child, #contentInner .cvn-os-shell .cvn-os-document td:last-child { border-right: 0; }
      #contentInner .cvn-os-shell .cvn-os-document tbody tr:last-child td { border-bottom: 0; }

      #contentInner .cvn-os-shell .cvn-ekc-chapter-nav { display: grid; grid-template-columns: 1fr auto 1fr; gap: 14px; align-items: stretch; box-sizing: border-box; width: 100%; max-width: 1000px; margin: 36px auto 0 !important; }
      #contentInner .cvn-os-shell .cvn-ekc-nav-card { display: block; box-sizing: border-box; min-width: 0; padding: 14px 18px !important; border: 1px solid rgba(135,159,188,.3); border-radius: 12px; background: rgba(255,255,255,.04); text-align: left; cursor: pointer; font-family: inherit; }
      #contentInner .cvn-os-shell .cvn-ekc-nav-card:hover, #contentInner .cvn-os-shell .cvn-ekc-nav-card:focus-visible { border-color: rgba(99,208,203,.45); background: rgba(99,208,203,.06); }
      #contentInner .cvn-os-shell .cvn-ekc-nav-next { text-align: right; }
      #contentInner .cvn-os-shell .cvn-ekc-nav-card small { display: block; margin: 0 0 4px !important; color: #8698ae; font-size: 10.5px; font-weight: 600; letter-spacing: .08em; text-transform: uppercase; }
      #contentInner .cvn-os-shell .cvn-ekc-nav-card span { display: block; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; color: #edf4ff; font-size: 14px; line-height: 1.4; }
      #contentInner .cvn-os-shell .cvn-ekc-nav-contents { align-self: center; padding: 14px 24px !important; border: 1px solid rgba(99,208,203,.4); border-radius: 12px; background: rgba(99,208,203,.1); color: #71d5d0; font-size: 14px; font-weight: 600; cursor: pointer; font-family: inherit; }
      #contentInner .cvn-os-shell .cvn-ekc-nav-contents:hover, #contentInner .cvn-os-shell .cvn-ekc-nav-contents:focus-visible { background: rgba(99,208,203,.18); }
      #contentInner .cvn-os-shell .cvn-ekc-nav-spacer { display: block; }

      .cvn-ekc-overlay-backdrop, .cvn-ekc-drawer-backdrop { position: fixed; inset: 0; z-index: 12000; display: flex; background: rgba(4,12,25,.7); backdrop-filter: blur(3px); }
      .cvn-ekc-overlay-backdrop { align-items: flex-start; justify-content: center; padding: 80px 20px; }
      .cvn-ekc-search-dialog { width: min(560px, 100%); max-height: 70vh; display: flex; flex-direction: column; border-radius: 14px; background: #0f2135; border: 1px solid rgba(99,208,203,.2); overflow: hidden; }
      .cvn-ekc-search-dialog-input { padding: 16px 18px; border: none; border-bottom: 1px solid rgba(135,159,188,.16); background: transparent; color: #fff; font-size: 15px; outline: none; }
      .cvn-ekc-search-results { overflow-y: auto; padding: 8px; }
      .cvn-ekc-search-empty { padding: 20px; color: #7f95ae; font-size: 13px; text-align: center; }
      .cvn-ekc-search-result-item { width: 100%; display: flex; flex-direction: column; align-items: flex-start; gap: 3px; text-align: left; padding: 10px 12px; border-radius: 9px; background: none; border: none; cursor: pointer; font-family: inherit; }
      .cvn-ekc-search-result-item:hover, .cvn-ekc-search-result-item:focus-visible { background: rgba(99,208,203,.08); }
      .cvn-ekc-search-result-context { color: #63d0cb; font-size: 10px; font-weight: 700; letter-spacing: .06em; text-transform: uppercase; }
      .cvn-ekc-search-result-title { color: #edf4ff; font-size: 14px; }

      .cvn-ekc-drawer-backdrop { align-items: stretch; justify-content: flex-end; }
      .cvn-ekc-drawer { width: min(340px, 90%); height: 100%; max-height: 100vh; display: flex; flex-direction: column; background: #0f2135; border-left: 1px solid rgba(99,208,203,.2); }
      .cvn-ekc-drawer-header { flex: 0 0 auto; padding: 18px 18px 12px; color: #edf4ff; font-size: 12px; font-weight: 700; letter-spacing: .1em; text-transform: uppercase; border-bottom: 1px solid rgba(135,159,188,.12); }
      .cvn-ekc-drawer-body { flex: 1 1 auto; overflow-y: auto; padding: 14px 16px; }
      .cvn-ekc-drawer-part { margin-bottom: 2px; }
      .cvn-ekc-drawer-part-header { width: 100%; text-align: left; padding: 9px 8px; border-radius: 7px; background: none; border: none; color: #cdd8e6; font-size: 11px; font-weight: 700; letter-spacing: .04em; text-transform: uppercase; cursor: pointer; font-family: inherit; }
      .cvn-ekc-drawer-part-header:hover, .cvn-ekc-drawer-part-header:focus-visible { background: rgba(99,208,203,.07); }
      .cvn-ekc-drawer-chapters { display: none; flex-direction: column; padding-left: 14px; border-left: 1px solid rgba(99,208,203,.12); margin: 2px 0 10px 10px; }
      .cvn-ekc-drawer-chapters.is-open { display: flex; }
      .cvn-ekc-drawer-chapter-btn { text-align: left; padding: 6px 9px; border-radius: 6px; background: none; border: none; color: #7c93ab; font-size: 11px; cursor: pointer; font-family: inherit; }
      .cvn-ekc-drawer-chapter-btn:hover, .cvn-ekc-drawer-chapter-btn:focus-visible { color: #71d5d0; }
      .cvn-ekc-drawer-chapter-btn[aria-current="true"] { color: #63d0cb; font-weight: 500; background: rgba(99,208,203,.08); }

      .cvn-ekc-search-dialog-input:focus-visible, .cvn-ekc-search-input:focus-visible, .cvn-ekc-icon-btn:focus-visible, .cvn-ekc-nav-card:focus-visible, .cvn-ekc-nav-contents:focus-visible, .cvn-ekc-index-row-main:focus-visible, .cvn-ekc-index-chapter-btn:focus-visible, .cvn-ekc-continue-btn:focus-visible, .cvn-ekc-back-btn:focus-visible, .cvn-ekc-drawer-chapter-btn:focus-visible, .cvn-ekc-drawer-part-header:focus-visible, .cvn-ekc-search-result-item:focus-visible { outline: 2px solid #63d0cb; outline-offset: 2px; }

      @media (max-width: 900px) {
        #contentInner .cvn-os-shell .cvn-ekc-canvas { padding: 32px 26px 40px !important; }
      }
      @media (max-width: 720px) {
        #contentInner .cvn-os-shell .cvn-ekc-table-scroll { overflow-x: auto !important; overflow-y: hidden !important; -webkit-overflow-scrolling: touch; }
        #contentInner .cvn-os-shell .cvn-os-document table { min-width: 560px !important; }
        #contentInner .cvn-os-shell .cvn-ekc-table-scroll::before { content: 'Swipe sideways to see all columns →'; display: block; position: sticky; left: 0; padding: 8px 12px; background: #EEF2F5; border-bottom: 1px solid #E1E7EC; color: #4a6076; font-size: 11px; letter-spacing: .02em; }
        #contentInner .cvn-os-shell .cvn-os-document th { position: static !important; }
      }
      @media (max-width: 640px) {
        #contentInner .cvn-os-shell .cvn-ekc-canvas { padding: 26px 18px 34px !important; border-radius: 12px; }
        #contentInner .cvn-os-shell .cvn-ekc-index-row-main { grid-template-columns: 40px 1fr 24px !important; }
        #contentInner .cvn-os-shell .cvn-ekc-index-meta { display: none; }
        #contentInner .cvn-os-shell .cvn-ekc-index-chapters { margin-left: 44px !important; }
        #contentInner .cvn-os-shell .cvn-ekc-back-btn { padding: 8px 11px !important; }
        #contentInner .cvn-os-shell .cvn-ekc-chapter-nav { position: sticky; bottom: 0; margin-top: 24px !important; padding: 10px !important; border-radius: 12px; background: rgba(11,20,38,.96); backdrop-filter: blur(6px); }
        #contentInner .cvn-os-shell .cvn-ekc-nav-card span { display: none; }
        #contentInner .cvn-os-shell .cvn-ekc-nav-card { padding: 12px 14px !important; }
        #contentInner .cvn-os-shell .cvn-ekc-nav-card small { margin: 0 !important; white-space: nowrap; letter-spacing: .04em; }
        #contentInner .cvn-os-shell .cvn-ekc-chapter-nav { gap: 8px !important; }
        .cvn-ekc-overlay-backdrop { padding: 0; align-items: stretch; }
        .cvn-ekc-search-dialog { width: 100%; max-height: 100vh; height: 100%; border-radius: 0; }
        .cvn-ekc-drawer { width: 100%; max-height: 80vh; align-self: flex-end; border-left: none; border-top: 1px solid rgba(99,208,203,.2); border-radius: 16px 16px 0 0; }
        .cvn-ekc-drawer-backdrop { align-items: flex-end; }
      }
    `;

    document.head.appendChild(style);
  }


  /* ============================================================
     RESTRAINED PART WATERMARKS — abstract line-art only, atmospheric
     and subordinate to the title text, never photographic/decorative.
     ============================================================ */

  function partWatermarkSVG(key) {
    const marks = {
      1: '<circle cx="70" cy="30" r="26" fill="none" stroke="currentColor" stroke-width="1"/><line x1="70" y1="4" x2="70" y2="56" stroke="currentColor" stroke-width="1"/><line x1="44" y1="30" x2="96" y2="30" stroke="currentColor" stroke-width="1"/>',
      2: '<rect x="50" y="0" width="14" height="60" fill="none" stroke="currentColor" stroke-width="1"/><rect x="70" y="16" width="14" height="44" fill="none" stroke="currentColor" stroke-width="1"/><rect x="90" y="30" width="14" height="30" fill="none" stroke="currentColor" stroke-width="1"/>',
      3: '<circle cx="55" cy="18" r="8" fill="none" stroke="currentColor" stroke-width="1"/><circle cx="80" cy="18" r="8" fill="none" stroke="currentColor" stroke-width="1"/><path d="M45 55 Q55 30 65 55" fill="none" stroke="currentColor" stroke-width="1"/><path d="M70 55 Q80 30 90 55" fill="none" stroke="currentColor" stroke-width="1"/>',
      4: '<circle cx="70" cy="30" r="22" fill="none" stroke="currentColor" stroke-width="1"/><circle cx="70" cy="30" r="6" fill="none" stroke="currentColor" stroke-width="1"/><line x1="70" y1="4" x2="70" y2="14" stroke="currentColor" stroke-width="1"/><line x1="70" y1="46" x2="70" y2="56" stroke="currentColor" stroke-width="1"/><line x1="44" y1="30" x2="54" y2="30" stroke="currentColor" stroke-width="1"/><line x1="86" y1="30" x2="96" y2="30" stroke="currentColor" stroke-width="1"/>',
      5: '<rect x="46" y="6" width="48" height="48" rx="2" fill="none" stroke="currentColor" stroke-width="1"/><line x1="46" y1="22" x2="94" y2="22" stroke="currentColor" stroke-width="1"/><line x1="46" y1="38" x2="94" y2="38" stroke="currentColor" stroke-width="1"/>',
      6: '<polyline points="46,50 58,32 70,40 84,14 94,20" fill="none" stroke="currentColor" stroke-width="1.2"/>',
      7: '<path d="M70 6 L78 24 L96 24 L82 36 L88 54 L70 42 L52 54 L58 36 L44 24 L62 24 Z" fill="none" stroke="currentColor" stroke-width="1"/>',
      appendix: '<path d="M40 12 h30 v42 h-30 z" fill="none" stroke="currentColor" stroke-width="1"/><path d="M70 12 h30 v42 h-30 z" fill="none" stroke="currentColor" stroke-width="1"/><line x1="70" y1="12" x2="70" y2="54" stroke="currentColor" stroke-width="1"/>'
    };
    return marks[key] || marks[1];
  }


  /* ============================================================
     LANDING VIEW
     ============================================================ */

  function renderChapterButtonHTML(s, lastKey) {
    const overview = isPartOverviewTitle(s.display_title);
    const parsed = parseChapterLabel(s.display_title);
    const current = s.section_key === lastKey;
    return `
      <button type="button" class="cvn-ekc-index-chapter-btn${current ? ' is-current' : ''}" data-section-key="${escapeHTML(s.section_key)}"${current ? ' aria-current="true"' : ''}>
        <span class="cvn-ekc-chapter-num">${overview ? '' : escapeHTML(parsed.num)}</span>
        <span class="${overview ? 'cvn-ekc-chapter-overview' : ''}">${escapeHTML(overview ? 'Part overview' : parsed.text)}</span>
      </button>
    `;
  }

  function renderPartIndexRowHTML(part, lastKey) {
    const isOpen = expandedPartNumber === part.partNumber;
    // The Part's own overview page is not a chapter, so it is not
    // counted as one (it still appears, labelled, inside the list).
    const chapterCount = part.sections.filter((s) => !isPartOverviewTitle(s.display_title)).length || part.sections.length;
    return `
      <div class="cvn-ekc-index-row${isOpen ? ' is-open' : ''}">
        <button type="button" class="cvn-ekc-index-row-main" data-part-number="${part.partNumber}" aria-expanded="${isOpen}">
          <span class="cvn-ekc-index-numeral">${escapeHTML(romanNumeralFor(part.partNumber))}</span>
          <span class="cvn-ekc-index-title">${escapeHTML(stripPartPrefix(part.title) || part.title)}</span>
          <span class="cvn-ekc-index-meta">${chapterCount} chapter${chapterCount === 1 ? '' : 's'}</span>
          <span class="cvn-ekc-index-chevron" aria-hidden="true">${isOpen ? '\u2212' : '+'}</span>
        </button>
        ${isOpen ? `
          <div class="cvn-ekc-index-chapters">
            ${part.sections.map((s) => renderChapterButtonHTML(s, lastKey)).join('')}
          </div>
        ` : ''}
      </div>
    `;
  }

  function renderAppendixIndexRowHTML(appendix) {
    const letter = (/^\s*APPENDIX\s+([A-Z])\b/i.exec(appendix.display_title) || [])[1];
    const title = String(appendix.display_title || '').replace(/^\s*APPENDIX\s+[A-Z]\s*[\u2014\u2013:\-]\s*/i, '').trim() || appendix.display_title;
    return `
      <div class="cvn-ekc-index-row">
        <button type="button" class="cvn-ekc-index-row-main" data-section-key="${escapeHTML(appendix.section_key)}" data-appendix="true">
          <span class="cvn-ekc-index-numeral cvn-ekc-index-numeral-small">App.${letter ? ' ' + escapeHTML(letter.toUpperCase()) : ''}</span>
          <span class="cvn-ekc-index-title">${escapeHTML(title)}</span>
          <span class="cvn-ekc-index-meta">Reference</span>
          <span class="cvn-ekc-index-chevron" aria-hidden="true">\u2192</span>
        </button>
      </div>
    `;
  }

  // "Continue where you left off" — reads only the last-viewed
  // section_key, a plain pointer with no content or credential value
  // of its own. Retrieving the actual section still requires the full
  // authenticated get-bible-content chain, same as any other
  // navigation; this only decides where to offer to jump to.
  function getContinueReadingSection() {
    let lastKey;
    try {
      lastKey = localStorage.getItem('cvnEkcLastSectionKey');
    } catch {
      return null;
    }
    if (!lastKey) return null;
    return getSectionByKey(lastKey);
  }

  function renderLanding() {
    currentView = 'landing';
    currentSectionKey = null;
    const root = document.getElementById('contentInner');
    if (!root) return;

    installStyles();

    const { parts, appendix } = groupSectionsByPart(currentSections);
    const continueSection = getContinueReadingSection();
    const lastKey = continueSection ? continueSection.section_key : null;

    root.innerHTML = `
      <section class="cvn-os-shell" id="cvnOperatingSystemTop">
        <div class="cvn-os-toolbar" aria-label="Operating System navigation">
          <button class="cvn-os-toolbar-btn" id="cvnOsDashboardButton" type="button">\u2190 Dashboard</button>
        </div>
        <main class="cvn-ekc-landing">
          <div class="cvn-ekc-eyebrow">${escapeHTML(currentRecord?.document_code || COS.documentCode)} \u00b7 Version ${escapeHTML(currentRecord?.version || COS.version)} \u00b7 Active controlled master \u00b7 CEO restricted</div>
          <h1 class="cvn-ekc-heading">The Corevian Bible<span class="cvn-ekc-heading-mark">\u2122</span></h1>
          <p class="cvn-ekc-subheading">Executive Knowledge Center</p>
          <div class="cvn-ekc-search-wrap">
            <button type="button" class="cvn-ekc-search-input" id="cvnEkcSearchTrigger" aria-haspopup="dialog">
              <span>Search the Corevian Bible\u2026</span>
              <span class="cvn-ekc-search-hint">Press / to search</span>
            </button>
          </div>
          ${continueSection ? `
            <button type="button" class="cvn-ekc-continue-btn" id="cvnEkcContinueBtn">
              <span class="cvn-ekc-continue-text">
                <span class="cvn-ekc-continue-label">Continue where you left off</span>
                <span class="cvn-ekc-continue-title">${escapeHTML(continueSection.display_title)}</span>
                <span class="cvn-ekc-continue-context">${escapeHTML(contextLabelFor(continueSection))}</span>
              </span>
              <span class="cvn-ekc-continue-arrow" aria-hidden="true">\u2192</span>
            </button>
          ` : ''}
          <div class="cvn-ekc-index" id="cvnEkcPartsGrid">
            ${parts.map((part) => renderPartIndexRowHTML(part, lastKey)).join('')}
            ${appendix ? renderAppendixIndexRowHTML(appendix) : ''}
          </div>
        </main>
      </section>
    `;

    bindWorkspaceToolbar();
    bindLandingEvents();
  }

  function bindLandingEvents() {
    document.getElementById('cvnEkcSearchTrigger')?.addEventListener('click', () => openSearchOverlay());

    document.getElementById('cvnEkcContinueBtn')?.addEventListener('click', () => {
      const section = getContinueReadingSection();
      if (section) renderReading(section);
    });

    const grid = document.getElementById('cvnEkcPartsGrid');
    if (!grid) return;

    grid.addEventListener('click', (event) => {
      const appendixBtn = event.target.closest('.cvn-ekc-index-row-main[data-appendix="true"]');
      if (appendixBtn) {
        const section = getSectionByKey(appendixBtn.dataset.sectionKey);
        if (section) renderReading(section);
        return;
      }

      const mainBtn = event.target.closest('.cvn-ekc-index-row-main');
      if (mainBtn) {
        const partNumber = Number(mainBtn.dataset.partNumber);
        expandedPartNumber = expandedPartNumber === partNumber ? null : partNumber;
        renderLanding();
        return;
      }

      const chapterBtn = event.target.closest('.cvn-ekc-index-chapter-btn');
      if (chapterBtn) {
        const section = getSectionByKey(chapterBtn.dataset.sectionKey);
        if (section) renderReading(section);
      }
    });
  }


  /* ============================================================
     READING VIEW — compact top context bar (breadcrumb + Search /
     Contents icons only, deliberately NOT a duplicate Previous/Next
     bar) plus one full Previous / Contents / Next control at the
     bottom, per the approved decision against redundant identical
     navigation bars.
     ============================================================ */

  function renderReading(section) {
    if (!section) return;

    currentView = 'reading';
    currentSectionKey = section.section_key;

    // Write side of "Continue where you left off" — stores only the
    // section_key, never content. See getContinueReadingSection() for
    // the read side and the reasoning.
    try {
      localStorage.setItem('cvnEkcLastSectionKey', section.section_key);
    } catch {
      // Storage unavailable (private browsing, quota, etc.) — the
      // reading experience itself does not depend on this succeeding.
    }

    const root = document.getElementById('contentInner');
    if (!root) return;

    installStyles();

    const index = currentSections.findIndex((s) => s.section_key === section.section_key);
    const prevSection = index > 0 ? currentSections[index - 1] : null;
    const nextSection = (index >= 0 && index < currentSections.length - 1) ? currentSections[index + 1] : null;
    const barLabel = `${partRomanLabel(section)} \u00b7 ${section.display_title}`;

    // FIX — content_html already supplies its own visible chapter
    // heading (this was true of the original, pre-Phase-3 renderer
    // too, which never manufactured a title of its own). The prior
    // build additionally rendered section.display_title as a
    // separate <h2>, producing a real duplicate visible heading. The
    // fix is presentation-only: no synthetic heading is rendered here
    // at all — content_html's own heading is the only one shown.
    root.innerHTML = `
      <section class="cvn-os-shell" id="cvnOperatingSystemTop">
        <div class="cvn-os-toolbar" aria-label="Operating System navigation">
          <button class="cvn-os-toolbar-btn" id="cvnOsDashboardButton" type="button">\u2190 Dashboard</button>
        </div>
        <div class="cvn-ekc-reading-shell">
          <div class="cvn-ekc-context-bar">
            <button type="button" class="cvn-ekc-back-btn" id="cvnEkcBreadcrumbHome">\u2190 Knowledge Center</button>
            <span class="cvn-ekc-context-label" title="${escapeHTML(barLabel)}">${escapeHTML(barLabel)}</span>
            <div class="cvn-ekc-context-actions">
              <button type="button" class="cvn-ekc-icon-btn" id="cvnEkcContentsIconBtn">Contents</button>
              <button type="button" class="cvn-ekc-icon-btn" id="cvnEkcSearchIconBtn">Search</button>
            </div>
          </div>
          <article class="cvn-ekc-canvas">
            <div class="cvn-ekc-canvas-badge">${escapeHTML(currentRecord?.document_code || COS.documentCode)} \u00b7 v${escapeHTML(currentRecord?.version || COS.version)} \u00b7 Controlled Master</div>
            <div class="cvn-os-document" id="cvnEkcDocumentBody">${sanitizeControlledHTML(section.content_html)}</div>
          </article>
          <nav class="cvn-ekc-chapter-nav" aria-label="Chapter navigation">
            ${prevSection ? `
              <button type="button" class="cvn-ekc-nav-card cvn-ekc-nav-prev" id="cvnEkcPrevBtn">
                <small>\u2190 Previous</small><span>${escapeHTML(sectionShortTitle(prevSection))}</span>
              </button>
            ` : '<span class="cvn-ekc-nav-spacer"></span>'}
            <button type="button" class="cvn-ekc-nav-contents" id="cvnEkcContentsBtnBottom">Contents</button>
            ${nextSection ? `
              <button type="button" class="cvn-ekc-nav-card cvn-ekc-nav-next" id="cvnEkcNextBtn">
                <small>Next \u2192</small><span>${escapeHTML(sectionShortTitle(nextSection))}</span>
              </button>
            ` : '<span class="cvn-ekc-nav-spacer"></span>'}
          </nav>
        </div>
      </section>
    `;

    // FIX — tables inside content_html previously had no scroll
    // wrapper, so a wide table forced the entire canvas (and page)
    // wider. This wraps each table, after sanitized insertion, in its
    // own horizontally-scrollable container without altering
    // content_html itself in any way — the table's markup and content
    // are untouched, only its DOM parent changes.
    const documentBody = document.getElementById('cvnEkcDocumentBody');
    if (documentBody) {
      documentBody.querySelectorAll('table').forEach((table) => {
        const wrapper = document.createElement('div');
        wrapper.className = 'cvn-ekc-table-scroll';
        table.parentNode.insertBefore(wrapper, table);
        wrapper.appendChild(table);
      });
    }

    bindWorkspaceToolbar();

    document.getElementById('cvnEkcBreadcrumbHome')?.addEventListener('click', () => renderLanding());
    document.getElementById('cvnEkcSearchIconBtn')?.addEventListener('click', () => openSearchOverlay());
    document.getElementById('cvnEkcContentsIconBtn')?.addEventListener('click', () => openContentsDrawer());
    document.getElementById('cvnEkcContentsBtnBottom')?.addEventListener('click', () => openContentsDrawer());
    if (prevSection) document.getElementById('cvnEkcPrevBtn')?.addEventListener('click', () => renderReading(prevSection));
    if (nextSection) document.getElementById('cvnEkcNextBtn')?.addEventListener('click', () => renderReading(nextSection));

    const shell = document.querySelector('.cvn-os-shell');
    if (shell) shell.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }


  /* ============================================================
     SEARCH OVERLAY — assist, not replacement. Never discards the
     primary view underneath; closing it (Escape, backdrop, or a
     result) simply removes the overlay. In-memory index only,
     built once per fetched content set, never persisted.
     ============================================================ */

  function openSearchOverlay() {
    if (activeOverlay) closeActiveOverlay();

    lastFocusedElement = document.activeElement;
    activeOverlay = 'search';

    const shell = document.querySelector('.cvn-os-shell');
    if (!shell) return;

    const wrap = document.createElement('div');
    wrap.className = 'cvn-ekc-overlay-backdrop';
    wrap.id = 'cvnEkcSearchOverlay';
    wrap.innerHTML = `
      <div class="cvn-ekc-search-dialog" role="dialog" aria-modal="true" aria-label="Search the Corevian Bible">
        <input type="text" class="cvn-ekc-search-dialog-input" id="cvnEkcSearchDialogInput" placeholder="Search the Corevian Bible\u2026" autocomplete="off">
        <div class="cvn-ekc-search-results" id="cvnEkcSearchResults"></div>
      </div>
    `;
    shell.appendChild(wrap);

    const input = document.getElementById('cvnEkcSearchDialogInput');
    const resultsEl = document.getElementById('cvnEkcSearchResults');

    function renderResults(query) {
      const q = query.trim().toLowerCase();
      if (!q) {
        resultsEl.innerHTML = '<div class="cvn-ekc-search-empty">Start typing to search the Corevian Bible.</div>';
        return;
      }
      const matches = searchIndex.filter((entry) => entry.haystack.includes(q)).slice(0, 20);
      if (!matches.length) {
        resultsEl.innerHTML = '<div class="cvn-ekc-search-empty">No matching sections.</div>';
        return;
      }
      resultsEl.innerHTML = matches.map((entry) => `
        <button type="button" class="cvn-ekc-search-result-item" data-section-key="${escapeHTML(entry.section.section_key)}">
          <span class="cvn-ekc-search-result-context">${escapeHTML(contextLabelFor(entry.section))}</span>
          <span class="cvn-ekc-search-result-title">${escapeHTML(entry.section.display_title)}</span>
        </button>
      `).join('');
    }

    renderResults('');
    input.addEventListener('input', () => renderResults(input.value));

    resultsEl.addEventListener('click', (event) => {
      const item = event.target.closest('.cvn-ekc-search-result-item');
      if (!item) return;
      const section = getSectionByKey(item.dataset.sectionKey);
      closeActiveOverlay();
      if (section) renderReading(section);
    });

    wrap.addEventListener('click', (event) => {
      if (event.target === wrap) closeActiveOverlay();
    });

    trapFocusWithin(wrap);
    input.focus();
  }


  /* ============================================================
     CONTENTS DRAWER — overlay only, never a permanent column.
     ============================================================ */

  function openContentsDrawer() {
    if (activeOverlay) closeActiveOverlay();

    lastFocusedElement = document.activeElement;
    activeOverlay = 'contents';

    const shell = document.querySelector('.cvn-os-shell');
    if (!shell) return;

    const { parts, appendix } = groupSectionsByPart(currentSections);
    const currentPart = currentPartNumberOf(currentSectionKey);

    const wrap = document.createElement('div');
    wrap.className = 'cvn-ekc-drawer-backdrop';
    wrap.id = 'cvnEkcContentsDrawer';
    wrap.innerHTML = `
      <div class="cvn-ekc-drawer" role="dialog" aria-modal="true" aria-label="Contents" tabindex="-1">
        <div class="cvn-ekc-drawer-header">Contents</div>
        <div class="cvn-ekc-drawer-body">
          ${parts.map((part) => `
            <div class="cvn-ekc-drawer-part">
              <button type="button" class="cvn-ekc-drawer-part-header" data-part-number="${part.partNumber}" aria-expanded="${part.partNumber === currentPart}">
                Part ${escapeHTML(romanPart(part.partNumber))} \u2014 ${escapeHTML(part.title)}
              </button>
              <div class="cvn-ekc-drawer-chapters${part.partNumber === currentPart ? ' is-open' : ''}" data-chapters-for="${part.partNumber}">
                ${part.sections.map((s) => `<button type="button" class="cvn-ekc-drawer-chapter-btn" data-section-key="${escapeHTML(s.section_key)}" ${s.section_key === currentSectionKey ? 'aria-current="true"' : ''}>${escapeHTML(s.display_title)}</button>`).join('')}
              </div>
            </div>
          `).join('')}
          ${appendix ? `
            <div class="cvn-ekc-drawer-part">
              <button type="button" class="cvn-ekc-drawer-chapter-btn" data-section-key="${escapeHTML(appendix.section_key)}" ${appendix.section_key === currentSectionKey ? 'aria-current="true"' : ''}>${escapeHTML(appendix.display_title)}</button>
            </div>
          ` : ''}
        </div>
      </div>
    `;
    shell.appendChild(wrap);

    wrap.addEventListener('click', (event) => {
      if (event.target === wrap) { closeActiveOverlay(); return; }

      const header = event.target.closest('.cvn-ekc-drawer-part-header');
      if (header) {
        const chaptersEl = wrap.querySelector(`[data-chapters-for="${header.dataset.partNumber}"]`);
        const isOpen = chaptersEl.classList.toggle('is-open');
        header.setAttribute('aria-expanded', String(isOpen));
        return;
      }

      const chapterBtn = event.target.closest('.cvn-ekc-drawer-chapter-btn');
      if (chapterBtn) {
        const section = getSectionByKey(chapterBtn.dataset.sectionKey);
        closeActiveOverlay();
        if (section) renderReading(section);
      }
    });

    trapFocusWithin(wrap);
    wrap.querySelector('.cvn-ekc-drawer')?.focus();
  }


  /* ============================================================
     SHARED OVERLAY HELPERS
     ============================================================ */

  function closeActiveOverlay() {
    if (!activeOverlay) return;
    const elementId = activeOverlay === 'search' ? 'cvnEkcSearchOverlay' : 'cvnEkcContentsDrawer';
    const el = document.getElementById(elementId);
    if (el) el.remove();
    activeOverlay = null;
    if (lastFocusedElement && typeof lastFocusedElement.focus === 'function') {
      lastFocusedElement.focus();
    }
    lastFocusedElement = null;
  }

  function trapFocusWithin(container) {
    container.addEventListener('keydown', (event) => {
      if (event.key !== 'Tab') return;
      const focusable = container.querySelectorAll('button, input, [tabindex]:not([tabindex="-1"])');
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });
  }

  function installGlobalKeyHandler() {
    if (globalKeyHandlerInstalled) return;
    globalKeyHandlerInstalled = true;

    document.addEventListener('keydown', (event) => {
      if (!document.body.classList.contains('cvn-os-workspace-active')) return;

      if (event.key === 'Escape') {
        if (activeOverlay) {
          event.preventDefault();
          closeActiveOverlay();
        }
        return;
      }

      if (event.key === '/' && !activeOverlay) {
        const tag = (event.target.tagName || '').toLowerCase();
        const isEditable = tag === 'input' || tag === 'textarea' || event.target.isContentEditable;
        if (isEditable) return;
        event.preventDefault();
        openSearchOverlay();
      }
    });
  }


  /* ============================================================
     ENTRY POINT — replaces the old renderOperatingSystem() call.
     ============================================================ */

  function initializeKnowledgeCenter(record, sections) {
    currentRecord = record;
    currentSections = Array.isArray(sections) ? sections.slice() : [];
    buildSearchIndex(currentSections);
    currentView = 'landing';
    currentSectionKey = null;
    expandedPartNumber = null;
    activeOverlay = null;

    installGlobalKeyHandler();
    renderLanding();
  }


  function renderViewerError(message) {
    const root = document.getElementById('contentInner');
    if (!root) return;

    installStyles();

    root.innerHTML = `
      <section class="cvn-os-shell">
        <div class="cvn-os-status">
          <strong>Corevian Operating System could not be opened.</strong>
          ${escapeHTML(message || 'The protected controlled content is unavailable.')}
        </div>
      </section>
    `;
  }




  /* ============================================================
     NEW: minimal TOTP challenge prompt — the one unavoidable new
     UI step. Rendered into the same #contentInner area, using the
     existing cvn-os-shell/cvn-os-status visual language rather than
     introducing a new design. Does not touch anything of the
     Operating System viewer itself, which only renders after this
     step succeeds.
     ============================================================ */

  function renderChallengePrompt(onSubmit, errorMessage) {
    const root = document.getElementById('contentInner');
    if (!root) return;

    installStyles();

    root.innerHTML = `
      <section class="cvn-os-shell">
        <div class="cvn-os-status">
          <strong>Authenticator verification required.</strong>
          Enter your current 6-digit code, or a recovery code, to open the Operating System.
          ${errorMessage ? `<div class="cvn-os-challenge-error">${escapeHTML(errorMessage)}</div>` : ''}
          <div class="cvn-os-challenge-field">
            <input class="cvn-os-challenge-input" id="cvnOsChallengeInput" type="text" autocomplete="one-time-code" placeholder="000000" maxlength="16">
            <br>
            <button class="cvn-os-challenge-btn" id="cvnOsChallengeSubmit" type="button">Continue</button>
          </div>
        </div>
      </section>
    `;

    document.getElementById('cvnOsChallengeSubmit')?.addEventListener('click', () => {
      const value = document.getElementById('cvnOsChallengeInput')?.value?.trim();
      if (!value) return;
      onSubmit(value);
    });
  }


  function mapErrorMessage(status, body) {
    if (status === 401) return 'Your session has expired. Please sign in again.';
    if (status === 403 && body?.error === 'forbidden') return 'CEO authorization required.';
    if (body?.error === 'locked_out') return 'Too many attempts. Please wait before trying again.';
    if (body?.error === 'attempt_already_in_progress') return 'A verification attempt is already in progress. Please wait a moment and try again.';
    if (body?.error === 'invalid_code') return 'That code was not recognized. Please try again.';
    return 'Secure controlled content could not be loaded.';
  }


  /* ============================================================
     SECURE OPEN — migrated. Demo Mode and CEO-authorization checks
     are unchanged and still run first. What changed: the two direct
     Supabase queries are replaced by get-bible-content, gated on a
     valid Bible session. If no held session works, the minimal
     challenge prompt above is shown, chaining request-bible-challenge
     and verify-bible-challenge (both already deployed) to obtain one,
     then the same get-bible-content call is retried. Once content is
     returned, rendering proceeds through initializeKnowledgeCenter() — the
     Phase 3 Executive Knowledge Center entry point — which is untouched
     by anything in this Layer A retry/challenge flow.
     ============================================================ */

  async function fetchAndRenderBibleContent() {
    // Try a held token first, if one exists from earlier in this page's life.
    if (heldBibleSessionToken) {
      const result = await callGetBibleContent(heldBibleSessionToken);
      if (result.ok) {
        installStyles();
        installExecutiveWorkspaceStyles();
        enterOperatingSystemWorkspace();
        initializeKnowledgeCenter(result.body.record, result.body.sections);
        return true;
      }
      // Held token no longer works for any reason — drop it and fall
      // through to a fresh challenge below.
      heldBibleSessionToken = null;
    }

    runChallengeAttempt(null);
    return true;
  }

  // Retry-eligible outcomes (a mistyped code, a lockout, an in-progress
  // attempt) re-render THIS SAME prompt with an inline error, so she
  // never has to reopen the Operating System just to try again. Only
  // genuine hard failures — expired auth, non-CEO, server errors —
  // exit to the existing renderViewerError view, since retrying a
  // code would not fix those.
  async function runChallengeAttempt(priorErrorMessage) {
    renderChallengePrompt(async (code) => {
      let challengeResult;
      try {
        challengeResult = await callRequestChallenge();
      } catch (error) {
        console.error('Corevian Operating System: request-bible-challenge failed:', error);
        renderViewerError('Secure controlled content could not be loaded.');
        return;
      }

      if (!challengeResult.ok) {
        if (isRetryEligible(challengeResult.body)) {
          runChallengeAttempt(mapErrorMessage(challengeResult.status, challengeResult.body));
          return;
        }
        renderViewerError(mapErrorMessage(challengeResult.status, challengeResult.body));
        return;
      }

      const attemptId = challengeResult.body?.attemptId;
      if (!attemptId) {
        renderViewerError('Secure controlled content could not be loaded.');
        return;
      }

      let verifyResult;
      try {
        verifyResult = await callVerifyChallenge(attemptId, code);
      } catch (error) {
        console.error('Corevian Operating System: verify-bible-challenge failed:', error);
        renderViewerError('Secure controlled content could not be loaded.');
        return;
      }

      if (!verifyResult.ok || !verifyResult.body?.bibleSessionToken) {
        if (isRetryEligible(verifyResult.body)) {
          runChallengeAttempt(mapErrorMessage(verifyResult.status, verifyResult.body));
          return;
        }
        renderViewerError(mapErrorMessage(verifyResult.status, verifyResult.body));
        return;
      }

      heldBibleSessionToken = verifyResult.body.bibleSessionToken;

      const contentResult = await callGetBibleContent(heldBibleSessionToken);
      if (!contentResult.ok) {
        heldBibleSessionToken = null;
        renderViewerError('Secure controlled content could not be loaded.');
        return;
      }

      installStyles();
      installExecutiveWorkspaceStyles();
      enterOperatingSystemWorkspace();
      initializeKnowledgeCenter(contentResult.body.record, contentResult.body.sections);
    }, priorErrorMessage);
  }

  function isRetryEligible(body) {
    return (
      body?.error === 'invalid_code' ||
      body?.error === 'locked_out' ||
      body?.error === 'attempt_already_in_progress'
    );
  }

  async function openCorevianOperatingSystem() {
    if (isDemoMode()) {
      console.info(
        'Corevian Operating System: unavailable while Executive Demo is active.'
      );
      return false;
    }

    try {
      const session = await getAuthenticatedSession();

      if (!isAuthorizedCEO(session)) {
        console.warn(
          'Corevian Operating System: CEO authorization required.'
        );
        return false;
      }

      await fetchAndRenderBibleContent();
      return true;

    } catch (error) {
      console.error('Corevian Operating System:', error);
      exitOperatingSystemWorkspace();
      renderViewerError('Secure controlled content could not be loaded.');
      return false;
    }
  }


  /* ============================================================
     INITIALIZE — unchanged from the existing file.
     ============================================================ */

  async function initializeOperatingSystem() {
    if (isDemoMode()) return;

    if (operatingSystemInstalled) return;

    try {
      const session = await getAuthenticatedSession();

      if (!isAuthorizedCEO(session)) return;

      operatingSystemInstalled = true;

      window.openCorevianOperatingSystem = openCorevianOperatingSystem;

    } catch (error) {
      console.error('Corevian Operating System initialization error:', error);
    }
  }


  /* ============================================================
     START — unchanged from the existing file.
     ============================================================ */

  function start() {
    setTimeout(initializeOperatingSystem, 1400);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }

  /* ============================================================
     EXECUTIVE WORKSPACE — unchanged from the existing file.
     ============================================================ */

  function enterOperatingSystemWorkspace() {
    document.body.classList.add('cvn-os-workspace-active');
    const sidebar = document.getElementById('sidebar');
    if (sidebar) sidebar.setAttribute('data-cvn-os-hidden', 'true');
    hideLegacyExecutiveLaunchers();
  }

  function exitOperatingSystemWorkspace() {
    document.body.classList.remove('cvn-os-workspace-active');
    const sidebar = document.getElementById('sidebar');
    if (sidebar) {
      sidebar.removeAttribute('data-cvn-os-hidden');
      sidebar.style.removeProperty('display');
    }
    restoreLegacyExecutiveLaunchers();
  }

  window.closeCorevianOperatingSystem = function () {
    exitOperatingSystemWorkspace();
    return true;
  };

  function installExecutiveWorkspaceStyles() {
    if (document.getElementById('cvnOperatingSystemExecutiveStyles')) return;
    const style = document.createElement('style');
    style.id = 'cvnOperatingSystemExecutiveStyles';
    style.textContent = `
      body.cvn-os-workspace-active #sidebar { display:none !important; }
      body.cvn-os-workspace-active #contentInner { width:100% !important; max-width:none !important; margin-left:0 !important; padding-left:28px !important; padding-right:28px !important; }
      body.cvn-os-workspace-active .cvn-os-shell { width:100% !important; max-width:1560px !important; margin:0 auto !important; padding:18px 0 70px !important; }
      .cvn-os-toolbar { position:sticky; top:8px; z-index:1000; display:flex; justify-content:flex-end; gap:8px; margin:0 0 12px; pointer-events:none; }
      .cvn-os-toolbar-btn { pointer-events:auto; border:1px solid rgba(99,208,203,.25); border-radius:10px; padding:9px 14px; background:#0d2033; color:#c9e8e6; font:inherit; font-size:12px; font-weight:700; cursor:pointer; box-shadow:0 8px 22px rgba(0,0,0,.18); }
      .cvn-os-toolbar-btn:hover { border-color:rgba(99,208,203,.5); background:#132b42; color:#fff; }
      body.cvn-os-workspace-active .cvn-os-hero { padding:24px 30px 22px !important; border-radius:18px !important; border:1px solid rgba(99,208,203,.20) !important; background:radial-gradient(circle at top right,rgba(99,208,203,.08),transparent 32%),linear-gradient(135deg,#11283e 0%,#0b1d30 58%,#091827 100%) !important; box-shadow:0 18px 42px rgba(0,0,0,.18) !important; }
      body.cvn-os-workspace-active .cvn-os-eyebrow { margin-bottom:7px !important; font-size:10px !important; letter-spacing:.20em !important; }
      body.cvn-os-workspace-active .cvn-os-title { max-width:1000px !important; font-size:clamp(34px,3vw,47px) !important; line-height:1.03 !important; }
      body.cvn-os-workspace-active .cvn-os-subtitle { margin-top:7px !important; font-size:15px !important; }
      body.cvn-os-workspace-active .cvn-os-security { margin-top:13px !important; padding:6px 11px !important; font-size:9px !important; }
      body.cvn-os-workspace-active .cvn-os-meta { grid-template-columns:1.35fr .8fr .65fr 1fr !important; gap:9px !important; margin-top:14px !important; }
      body.cvn-os-workspace-active .cvn-os-meta-card { min-height:64px !important; padding:11px 14px !important; border-radius:12px !important; background:rgba(255,255,255,.025) !important; }
      body.cvn-os-workspace-active .cvn-os-meta-label { margin-bottom:5px !important; font-size:9px !important; }
      body.cvn-os-workspace-active .cvn-os-meta-value { font-size:12px !important; line-height:1.35 !important; }
      body.cvn-os-workspace-active .cvn-os-layout { grid-template-columns:305px minmax(0,1fr) !important; gap:16px !important; margin-top:16px !important; }
      body.cvn-os-workspace-active .cvn-os-nav, body.cvn-os-workspace-active .cvn-os-content { border-radius:15px !important; border:1px solid rgba(135,159,188,.14) !important; background:#102238 !important; }
      body.cvn-os-workspace-active .cvn-os-nav { top:16px !important; padding:15px 13px !important; max-height:calc(100vh - 32px) !important; }
      body.cvn-os-workspace-active .cvn-os-nav-label { padding:0 7px 10px !important; margin-bottom:6px !important; font-size:9px !important; }
      body.cvn-os-workspace-active .cvn-os-nav-item { margin-bottom:4px !important; padding:9px 10px !important; border-radius:8px !important; font-size:11px !important; line-height:1.4 !important; white-space:normal !important; }
      body.cvn-os-workspace-active .cvn-os-part-button { margin-top:8px !important; padding-top:10px !important; padding-bottom:10px !important; font-size:10.5px !important; font-weight:750 !important; }
      body.cvn-os-workspace-active .cvn-os-chapter-list { margin:0 0 7px 4px !important; padding-left:7px !important; border-left:1px solid rgba(99,208,203,.14) !important; }
      body.cvn-os-workspace-active .cvn-os-chapter-button { padding:8px 9px 8px 11px !important; font-size:10px !important; color:#8fa5bd !important; }
      body.cvn-os-workspace-active .cvn-os-content { padding:30px 38px 46px !important; min-height:720px !important; }
      body.cvn-os-workspace-active .cvn-os-content-head { margin-bottom:22px !important; padding-bottom:14px !important; }
      body.cvn-os-workspace-active .cvn-os-content-head .cvn-os-content-title { display:none !important; }
      body.cvn-os-workspace-active .cvn-os-content-kicker { margin-bottom:6px !important; font-size:9px !important; }
      body.cvn-os-workspace-active .cvn-os-content-meta { margin-top:0 !important; font-size:10px !important; }
      /* The old cosmetic overrides that used to live here — for
         .cvn-os-document color/table/heading sizing — were written for
         the pre-Phase-3 dark document view and have been removed. They
         are not workspace lifecycle behavior (they never touched
         entering/exiting, sidebar hiding, or launcher restoration,
         which remain exactly as they were); they were conflicting
         !important color/sizing rules for a document style system that
         no longer exists, discovered to be silently overriding the
         current light-canvas typography this entire time. */
      body.cvn-os-workspace-active .cvn-bible-part-label { margin-bottom:10px !important; font-size:9px !important; }
      body.cvn-os-workspace-active .cvn-bible-part-intro { max-width:900px !important; margin-bottom:22px !important; font-size:14px !important; }
      body.cvn-os-workspace-active #cvnExecutiveCommandLauncher,
      body.cvn-os-workspace-active #cvnAdminDocumentsLauncher,
      body.cvn-os-workspace-active .cvn-executive-command-launcher,
      body.cvn-os-workspace-active .cvn-admin-documents-launcher,
      body.cvn-os-workspace-active button[aria-label="Executive Command Center"],
      body.cvn-os-workspace-active a[aria-label="Executive Command Center"],
      body.cvn-os-workspace-active button[title="Executive Command Center"],
      body.cvn-os-workspace-active a[title="Executive Command Center"],
      body.cvn-os-workspace-active button[aria-label="Admin Documents"],
      body.cvn-os-workspace-active a[aria-label="Admin Documents"],
      body.cvn-os-workspace-active button[title="Admin Documents"],
      body.cvn-os-workspace-active a[title="Admin Documents"] { display:none !important; }
      @media (max-width:1100px) { body.cvn-os-workspace-active .cvn-os-layout { grid-template-columns:270px minmax(0,1fr) !important; } }
      @media (max-width:900px) {
        body.cvn-os-workspace-active #contentInner { padding-left:12px !important; padding-right:12px !important; }
        body.cvn-os-workspace-active .cvn-os-layout { grid-template-columns:1fr !important; }
        body.cvn-os-workspace-active .cvn-os-nav { position:static !important; max-height:none !important; }
        body.cvn-os-workspace-active .cvn-os-meta { grid-template-columns:repeat(2,minmax(0,1fr)) !important; }
      }
      @media (max-width:560px) {
        body.cvn-os-workspace-active .cvn-os-hero { padding:23px 18px 20px !important; }
        body.cvn-os-workspace-active .cvn-os-title { font-size:32px !important; }
        body.cvn-os-workspace-active .cvn-os-meta { grid-template-columns:1fr !important; }
        body.cvn-os-workspace-active .cvn-os-content { padding:23px 18px 36px !important; }
        .cvn-os-toolbar { top:6px; }
      }
    `;
    document.head.appendChild(style);
  }

  function hideLegacyExecutiveLaunchers() {
    document.querySelectorAll('button, a, [role="button"]').forEach((element) => {
      const label = (element.textContent || '').replace(/\s+/g, ' ').trim();
      if (label === 'Executive Command Center' || label === 'Admin Documents') {
        element.setAttribute('data-cvn-legacy-executive-launcher', 'true');
        element.style.setProperty('display', 'none', 'important');
      }
    });
  }

  function restoreLegacyExecutiveLaunchers() {
    document.querySelectorAll('[data-cvn-legacy-executive-launcher="true"]').forEach((element) => {
      element.style.removeProperty('display');
      element.removeAttribute('data-cvn-legacy-executive-launcher');
    });
  }

  function goToUniversityDashboard() {
    exitOperatingSystemWorkspace();
    const dashboardButton = document.getElementById('navDash');
    if (dashboardButton) {
      dashboardButton.click();
      return;
    }
    if (typeof window.showView === 'function') {
      window.showView('dashboard');
    }
  }

  function bindWorkspaceToolbar() {
    const dashboard = document.getElementById('cvnOsDashboardButton');
    const top = document.getElementById('cvnOsTopButton');
    if (dashboard) dashboard.addEventListener('click', goToUniversityDashboard);
    if (top) top.addEventListener('click', () => {
      const target = document.getElementById('cvnOperatingSystemTop');
      if (target) target.scrollIntoView({ behavior:'smooth', block:'start' });
      else window.scrollTo({ top:0, behavior:'smooth' });
    });
  }

  function installGlobalExitGuard() {
    document.addEventListener('click', (event) => {
      if (!document.body.classList.contains('cvn-os-workspace-active')) return;
      const control = event.target.closest('button, a, [role="button"]');
      if (!control) return;
      if (control.closest('.cvn-os-shell')) return;
      if (control.id === 'navCorevianOperatingSystem') return;
      if (control.closest('#cvnExecutiveOperationsNavBlock') && control.id === 'navCorevianOperatingSystem') return;
      exitOperatingSystemWorkspace();
    }, true);
  }

  function installAuthCleanup() {
    const client = getUniversityClient();
    if (!client) {
      setTimeout(installAuthCleanup, 500);
      return;
    }
    client.auth.onAuthStateChange((event) => {
      if (event === 'SIGNED_OUT') {
        heldBibleSessionToken = null;
        exitOperatingSystemWorkspace();
      }

      // FIX — the launcher registration in initializeOperatingSystem()
      // previously ran exactly once, on a fixed 1400ms timer, with no
      // retry at all. If Supabase's own session rehydration from
      // storage hadn't finished by that exact moment (a real,
      // timing-dependent race on a hard refresh), getAuthenticatedSession()
      // returned null, isAuthorizedCEO(null) was false, and the
      // function returned silently — permanently, since nothing ever
      // asked it to try again. This confirmed CEO-account production
      // failure (nav item visible, launcher undefined, no error
      // logged) matches that exact failure mode.
      //
      // The fix touches ONLY timing, not authorization: these events
      // (SIGNED_IN fires once the SDK has a session; INITIAL_SESSION
      // fires once it finishes restoring one from storage on page
      // load, which is exactly the race above; TOKEN_REFRESHED covers
      // a session becoming valid again after a refresh) simply give
      // initializeOperatingSystem() another real chance to run its
      // SAME, unchanged isDemoMode()/isAuthorizedCEO() checks once a
      // session genuinely exists — rather than only ever guessing once
      // at 1400ms. initializeOperatingSystem() is itself unchanged and
      // safe to call more than once: operatingSystemInstalled guards
      // against redundant work once it has already succeeded.
      if (
        event === 'SIGNED_IN' ||
        event === 'INITIAL_SESSION' ||
        event === 'TOKEN_REFRESHED'
      ) {
        initializeOperatingSystem();
      }
    });
  }

  installExecutiveWorkspaceStyles();
  installGlobalExitGuard();
  setTimeout(installAuthCleanup, 1800);

})();